/* eslint-disable no-param-reassign */
import Workflow, { WorkflowActivity, WorkflowViewModel } from '../models/be/Workflow';
import { Section } from '../models/be/db/FormSpecification';
import Option from '../models/Option';
import { hasValue, getDateInDayMonthYearFormat, nestedArrayReducer } from '../lib/utils';
import {
	ds,
	dbs,
	vevo,
	ref,
	crim,
	vcrm,
	idv,
	aml,
	advr,
	sanc,
	pep,
	mpep,
	msanc,
	madvr,
	maml,
	pidv,
	parentCreditChecks,
	ccidv,
	auCountryCode,
	ukCountryCode,
	auParentCreditChecks,
	auChildCreditChecks,
	audir,
	auban,
	aucrd,
	aucom,
	refPlus,
	acic,
	ukChildCreditChecks,
	nzmoj,
	std,
	enh,
	customChecks,
	isMitekActivity,
	dbse,
	dbss,
	dbsev,
	dbssv,
	dbsa,
	cidv,
	offr,
	disq,
	oneDayInMilliseconds,
	dco,
	ukviShareCodeProvidedPleaseVerify
} from './constants';
import { finishedStatuses, Status, CheckType as REFCheckType } from './enums';
import CheckType from '../models/CheckType';
import CandidateActionWorkflow from '../models/be/CandidateActionWorkflow';
import { getCustomerUserIdFromAccessToken } from './session';
import { getCustomerUser } from './coreApi';
import { getWorkflow } from './api';
import { getCountryName } from './countryList';

export const getDisplayWorkflowTypeCode = (workflow: WorkflowViewModel, childCheckNumber: number = undefined) => {
	return hasValue(childCheckNumber)
		? `${getCheckIconDisplayNameForChildWorkflow(workflow, workflow.workflowTypeCode, childCheckNumber)}`
		: workflow.displayWorkflowTypeCode ?? workflow.workflowTypeCode;
};

// TODO: maybe move this into it's own check.ts file in the future.
export const getDisplayWorkflowTypeCodeFromCheckType = (checkType: CheckType) => {
	if (!checkType) return null;
	return checkType.displayWorkflowTypeCode ?? checkType.workflowCheckTypeCode;
};

const getCheckIconDisplayNameForChildWorkflow = (
	workflow: WorkflowViewModel,
	workflowTypeCode: string,
	childCheckNumber: number = undefined
) => {
	if (isChildRefWorkflow(workflowTypeCode)) {
		const parsedWorkflow = (workflow?.workflowDocument && JSON.parse(workflow.workflowDocument)) as Workflow | null;
		const childReference = parsedWorkflow?.childReference || workflow?.childReference;

		const { firstName = '', lastName = '' } = childReference?.capturedReferenceDetails || {};
		const refereeInitials = `${firstName[0] || ''}${lastName[0] || ''}`;

		return refereeInitials.toLocaleUpperCase() || (childCheckNumber !== 0 ? `${ref} ${childCheckNumber}` : ref);
	}
	if (isChildCreditWorkflow(workflowTypeCode)) {
		return 'CRD';
	}
	throw new Error(`Unknown child workflow type code: ${workflowTypeCode}.`);
};

export const isIdvWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === idv;
};

export const isPidvWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === pidv;
};

export const isCrimWorkflow = (workflow: Workflow | WorkflowViewModel | CandidateActionWorkflow) => {
	return workflow?.activities?.some((x) => x.providerType.serviceRequestType === acic) ?? false;
};

export const isTrueCrimWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === crim || workflowTypeCode === vcrm;
};

export const isRefWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === ref || workflowTypeCode === refPlus;
};

export const isBasicRef = (workflowTypeCode: string) => {
	return workflowTypeCode === ref;
};

export const isAdvancedRef = (workflowTypeCode: string) => {
	return workflowTypeCode === refPlus;
};

export const isChildRefWorkflow = (workflowTypeCode: string) => {
	const referenceCheckTypes = ['EREF', 'EDREF', 'SEREF', 'AREF', 'PREF', 'CREF'];
	return referenceCheckTypes.includes(workflowTypeCode);
};

export const hasCareerBreak = (workflow: Workflow): boolean => {
	return workflow?.referenceWorkflow?.references.some((r) => r.careerBreakEvidence !== null);
};

export const isChildCreditWorkflow = (workflowTypeCode: string) => {
	// All Child Credit Checks have a workflow type code that is: Parent WorkflowTypeCode + 'C'.
	return (
		workflowTypeCode?.toLowerCase().endsWith('c') &&
		parentCreditChecks.includes(workflowTypeCode.slice(0, workflowTypeCode.length - 1))
	);
};

export const containsRefWorkflow = (workflowTypeCodes: string[]) =>
	workflowTypeCodes.includes(ref) || workflowTypeCodes.includes(refPlus);

export const isVevoWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === vevo;
};

export const isDbsWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === dbs;
};

export const isDsWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === ds || workflowTypeCode === 'BDIS'; // May need to remove BDIS after UK fix the DS type issue.
};

export const isCaWorkflow = (workflowTypeCode: string) => {
	const complyAdvantageChecks = [aml, advr, sanc, pep];
	return complyAdvantageChecks.includes(workflowTypeCode);
};

export const isMitekSearchWorkflow = (workflowTypeCode: string) => {
	const mitekSearchChecks = [mpep, msanc, madvr, maml];
	return mitekSearchChecks.includes(workflowTypeCode);
};

export const isAudirWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === audir;
};

export const isAubanWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === auban;
};

export const isAucrdWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === aucrd;
};

export const isAucomWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === aucom;
};

export const isNzmojWorkflow = (workflowTypeCode: string) => {
	return workflowTypeCode === nzmoj;
};

export const isDbsStandardWorkflow = (workflowTypeCode: string) => workflowTypeCode === std;

export const isDbsEnhancedWorkflow = (workflowTypeCode: string) => workflowTypeCode === enh;

export const isDbsStandardOrEnhancedWorkflow = (workflowTypeCode: string) => {
	const firstAdvantageChecks = [dbse, dbss, dbsev, dbssv, dbsa];
	return firstAdvantageChecks.includes(workflowTypeCode);
};

// WorkflowViewModel doesn't carry customFields, so we identify this specific Attention Required
// reason by the last event log detail the backend sets when an unverified share code is present.
export const hasUnverifiedUkviShareCode = (workflow: WorkflowViewModel) =>
	workflow?.attentionRequired === true && workflow?.lastEventDetails === ukviShareCodeProvidedPleaseVerify;

export const isOfficersCheckWorkflow = (workflowTypeCode: string) => {
	const companiesHouseChecks = [offr, disq];
	return companiesHouseChecks.includes(workflowTypeCode);
};

export const isParentWorkflow = (workflowTypeCode: string) => {
	return isRefWorkflow(workflowTypeCode) || isParentCreditWorkflow(workflowTypeCode);
};

export const isAuParentCreditWorkflow = (workflowTypeCode: string) => {
	return auParentCreditChecks.includes(workflowTypeCode);
};

export const isAuChildCreditWorkflow = (workflowTypeCode: string) => {
	return auChildCreditChecks.includes(workflowTypeCode);
};

export const isUkChildCreditWorkflow = (workflowTypeCode: string) => {
	return ukChildCreditChecks.includes(workflowTypeCode);
};

export const isParentCreditWorkflow = (workflowTypeCode: string) => {
	return parentCreditChecks.includes(workflowTypeCode);
};

export const getIdvActivity = (activities: WorkflowActivity[]) => {
	return activities.find((x) => x.providerType.serviceRequestType === ccidv);
};

export const getUkrtwActivity = (activities: WorkflowActivity[]) => {
	return activities.find((x) => isMitekActivity(x.providerType.serviceProvider));
};

export const getConnectIdActivity = (activities: WorkflowActivity[]) => {
	return activities.find((x) => x.providerType.serviceRequestType === cidv);
};

export const getIdvResultMatchPercentage = (workflow: Workflow) => {
	const idvCheckResult = getIdvActivity(workflow.activities)?.check;
	if (hasValue(idvCheckResult) && hasValue(idvCheckResult.idvResult))
		return `${idvCheckResult.idvResult.matchPercentage}%`;
	return 'N/A - User has failed to capture images correctly.';
};

export const getLastEventTimeDescription = (workflow: WorkflowViewModel) => {
	// Dates must be any type to apply math
	const lastEvent = workflow.lastEventDetails;

	if (workflow.lastEventDate == null) return `${lastEvent}`;

	const lastEventDate = new Date(workflow.lastEventDate).getTime();
	const now = new Date().getTime();
	// solve below with math
	const minute = 60;
	const hour = minute * 60;
	const day = hour * 24;
	const week = day * 7;
	const dateDifferenceInSeconds = Math.floor((now - lastEventDate) / 1000);

	if (dateDifferenceInSeconds < minute) return `Moments ago: ${lastEvent}`;

	if (dateDifferenceInSeconds < hour) {
		const timeAgo = Math.floor(dateDifferenceInSeconds / minute);
		let time = 'minutes';
		if (timeAgo === 1) time = 'minute';
		return `${timeAgo} ${time} ago: ${lastEvent}`;
	}

	if (dateDifferenceInSeconds < day) {
		const timeAgo = Math.floor(dateDifferenceInSeconds / hour);
		let time = 'hours';
		if (timeAgo === 1) time = 'hour';
		return `${timeAgo} ${time} ago: ${lastEvent}`;
	}

	if (dateDifferenceInSeconds < week) {
		const timeAgo = Math.floor(dateDifferenceInSeconds / day);
		let time = 'days';
		if (timeAgo === 1) time = 'day';
		return `${timeAgo} ${time} ago: ${lastEvent}`;
	}

	return `${getDateInDayMonthYearFormat(new Date(lastEventDate))}: ${lastEvent}`;
};

export const isCompletedParent = (workflowTypeCode: string, status: Status) =>
	isParentWorkflow(workflowTypeCode) && status === Status.Completed;

export const getWorkflowStatusOrOutcome = (workflow: WorkflowViewModel) => {
	if (workflow.status === Status.Completed)
		return isParentWorkflow(workflow.workflowTypeCode) ? Status.Completed : workflow.outcome;
	if (!finishedStatuses.includes(workflow.status) && workflow.attentionRequired) return Status.AttentionRequired;
	return workflow.status;
};

export const getCreditCheckTargetCountryCode = (workflow: Workflow) => {
	return isAuParentCreditWorkflow(workflow.workflowTypeCode) ? auCountryCode : ukCountryCode;
};

export const isAcicCheckComplete = (workflow: WorkflowViewModel | CandidateActionWorkflow) => {
	return workflow?.activities?.find((x) => x.providerType.serviceRequestType === acic)?.status === Status.Completed;
};

const isWithinTwoDays = (dcoDate: Date) => dcoDate?.getTime() <= oneDayInMilliseconds * 2;

export const shouldHideAcicCertificate = async (workflow: WorkflowViewModel | CandidateActionWorkflow) => {
	const userId = getCustomerUserIdFromAccessToken();
	if (userId) {
		const [customerUser, completeWorkflow] = await Promise.all([
			getCustomerUser(userId),
			await getWorkflow(workflow.id)
		]);
		const acicActivity = workflow.activities.find((x) => x.providerType.serviceRequestType === acic);
		const disclosureOutcome = acicActivity?.check?.acicData?.result?.type;
		const acicStatus = acicActivity?.check?.status;
		const dcoResultDate = hasValue(workflow?.checkCompletedDate)
			? new Date(new Date().getTime() - new Date(workflow?.checkCompletedDate).getTime())
			: null;
		const hasDCO = hasValue(disclosureOutcome) && disclosureOutcome === dco;
		// 1. User Access Country restriction. hide if user is outside Australia
		if (customerUser.accessCountry !== auCountryCode) return true;

		// 2. Scrubbed workflow – always hide
		if (completeWorkflow.scrubbed) return true;

		// 3. No disclosure outcome – no reason to hide
		if (!hasDCO) return false;

		// 4. Status is "Held" – always hide
		if (acicStatus === 'Held') return true;

		// 5. Within 2 days – hide for everyone
		if (dcoResultDate && isWithinTwoDays(dcoResultDate)) return true;

		// 6. Sensitive content access – hide if user lacks sensitive content access permission
		if (!customerUser.hasSensitiveContentAccess) return true;
	}
	return false;
};

export const getWorkflowNameForScreener = (workflow: Workflow, childOrdinal?: number) => {
	if (childOrdinal) {
		const workflowServiceName = isChildRefWorkflow(workflow.workflowTypeCode)
			? 'Reference'
			: workflow.workflowServiceName;
		return `${workflowServiceName} ${childOrdinal}`;
	}
	if (workflow?.workflowTypeCode) {
		if (isCrimWorkflow(workflow)) return 'Nationally Coordinated Criminal History Check';
		const internationalCheckCountry = getCountryName(
			workflow.icrdDetails?.creditCheckCountry ?? workflow.icrmDetails?.criminalCheckCountry
		);
		if (hasValue(internationalCheckCountry)) {
			return workflow.workflowServiceName.replace('International', internationalCheckCountry);
		}
		return workflow.workflowServiceName;
	}

	return '';
};

export const replaceWorkflow = (workflows: Workflow[], updateWorkflows: Workflow[]) => {
	return workflows.map((w) => {
		return updateWorkflows.find((i) => i.id === w.id) ?? w;
	});
};

export const isCustomWorkflow = (workflowTypeCode: string) => {
	return customChecks.includes(workflowTypeCode);
};

export const isREFSubmittedWorkflow = (workflowTypeCode: string, status: string) => {
	return isRefWorkflow(workflowTypeCode) && (status === Status.Waiting || status === Status.InProgress);
};

export const filterOutWorkflowTypeCodesThatDontHaveTemplates = (
	referenceFormSpecification: Section[],
	workflow: Workflow
) => {
	const NotAvailableToCandidate = 'NotAvailableToCandidate';
	// Can we do this code using ASTs in the future?
	const workflowTypeCodeFields = referenceFormSpecification
		.map((s) => s.components)
		.reduce(nestedArrayReducer)
		.filter((c) => c.fieldNames.includes('workflowTypeCode'));
	workflowTypeCodeFields.forEach((workflowTypeCodeField) => {
		if (
			workflow.referenceWorkflow.agencyReferenceTemplateId === null ||
			workflow.referenceWorkflow.agencyReferenceTemplateId === NotAvailableToCandidate
		) {
			const index = workflowTypeCodeField.props.values.findIndex(
				(v) => (v as Option<string>).value === REFCheckType.Aref
			);
			workflowTypeCodeField.props.values.splice(index, 1);
		}
		if (
			workflow.referenceWorkflow.educationReferenceTemplateId === null ||
			workflow.referenceWorkflow.educationReferenceTemplateId === NotAvailableToCandidate
		) {
			const index = workflowTypeCodeField.props.values.findIndex(
				(v) => (v as Option<string>).value === REFCheckType.Edref
			);
			workflowTypeCodeField.props.values.splice(index, 1);
		}
		if (
			workflow.referenceWorkflow.employmentReferenceTemplateId === null ||
			workflow.referenceWorkflow.employmentReferenceTemplateId === NotAvailableToCandidate
		) {
			const index = workflowTypeCodeField.props.values.findIndex(
				(v) => (v as Option<string>).value === REFCheckType.Eref
			);
			workflowTypeCodeField.props.values.splice(index, 1);
		}
		if (
			workflow.referenceWorkflow.personalReferenceTemplateId === null ||
			workflow.referenceWorkflow.personalReferenceTemplateId === NotAvailableToCandidate
		) {
			const index = workflowTypeCodeField.props.values.findIndex(
				(v) => (v as Option<string>).value === REFCheckType.Pref
			);
			workflowTypeCodeField.props.values.splice(index, 1);
		}
		if (
			workflow.referenceWorkflow.selfEmployedReferenceTemplateId === null ||
			workflow.referenceWorkflow.selfEmployedReferenceTemplateId === NotAvailableToCandidate
		) {
			const index = workflowTypeCodeField.props.values.findIndex(
				(v) => (v as Option<string>).value === REFCheckType.Seref
			);
			workflowTypeCodeField.props.values.splice(index, 1);
		}
	});
};
